import type { DesignAppResult } from '@/types/design-app.types';

/** An output with nothing to show yet: still being made, or failed. Sits in its batch like a result. */
export type ToolOutputPlaceholder = {
  placeholder: 'loading' | 'failed';
  key: string;
  batchId: string;
  runStartedAt: string;
  /** Why it failed, when the server said. */
  message?: string;
  /** Width over height of the result the run asked for, when known. */
  aspectRatio?: number;
};

export type ToolOutputEntry = DesignAppResult | ToolOutputPlaceholder;

export const isToolOutputPlaceholder = (entry: ToolOutputEntry): entry is ToolOutputPlaceholder =>
  'placeholder' in entry;

export type ToolOutputGroup = {
  key: string;
  batchId: DesignAppResult['batchId'];
  createdAt: string | undefined;
  results: { result: DesignAppResult; resultIndex: number }[];
  /** Results and placeholders together, in the order they came in. */
  entries: ToolOutputEntry[];
};

/**
 * Groups outputs by batchId so every run started by one Run click shares a section.
 * Preserves output order and original indices within each group, uses the earliest
 * run start as its timestamp, and returns groups newest first with undated groups last.
 */
export function groupToolOutputs(outputs: readonly ToolOutputEntry[]): ToolOutputGroup[] {
  const groups = new Map<string, ToolOutputGroup>();
  outputs.forEach((entry, resultIndex) => {
    const key = `batch:${entry.batchId}`;
    const time = Date.parse(entry.runStartedAt ?? '');
    const createdAt = Number.isFinite(time) ? entry.runStartedAt : undefined;
    let group = groups.get(key);
    if (!group) {
      group = { key, batchId: entry.batchId, createdAt, results: [], entries: [] };
      groups.set(key, group);
    } else if (createdAt && (!group.createdAt || time < Date.parse(group.createdAt))) {
      group.createdAt = createdAt;
    }
    group.entries.push(entry);
    if (!isToolOutputPlaceholder(entry)) {
      group.results.push({ result: entry, resultIndex });
    }
  });
  const timestamp = (group: ToolOutputGroup) => Date.parse(group.createdAt ?? '') || 0;
  return [...groups.values()].sort((a, b) => timestamp(b) - timestamp(a));
}
